import { useEffect } from 'react';

const SITE_NAME = 'Kourse';
const SITE_URL = 'https://kourse.fr';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION = 'Découvrez et réservez votre place dans les meilleurs événements de course à pied.';

interface EventData {
  name?: string;
  startDate?: string;
  startTime?: string;
  location?: string;
  city?: string;
  description?: string;
  priceCents?: number;
  currency?: string;
  maxParticipants?: number;
  currentParticipants?: number;
  organizerName?: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface ItemListItem {
  name: string;
  url: string;
  image?: string;
  position?: number;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface OrganizationData {
  name: string;
  description?: string;
  url?: string;
  logo?: string;
  email?: string;
  telephone?: string;
  address?: {
    city?: string;
    country?: string;
  };
  sameAs?: string[];
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'event';
  eventData?: EventData;
  breadcrumbs?: BreadcrumbItem[];
  itemList?: ItemListItem[];
  faqItems?: FAQItem[];
  organizationData?: OrganizationData;
}

// Helper to update or create a meta tag
function updateMetaTag(property: string, content: string | undefined, isName = false) {
  if (!content) return;
  
  const attribute = isName ? 'name' : 'property';
  let element = document.querySelector(`meta[${attribute}="${property}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, property);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

// Helper to update canonical link
function updateCanonicalLink(url: string | undefined) {
  if (!url) return;
  
  let link = document.querySelector('link[rel="canonical"]');
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', url);
}

// Helper to update JSON-LD structured data
function updateJsonLd(
  eventData: EventData | undefined, 
  url: string | undefined,
  breadcrumbs: BreadcrumbItem[] | undefined,
  itemList: ItemListItem[] | undefined,
  faqItems: FAQItem[] | undefined,
  organizationData: OrganizationData | undefined
) {
  // Remove existing JSON-LD
  document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach(el => el.remove());
  
  // Add Event JSON-LD
  if (eventData?.name) {
    const eventJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: eventData.name,
      description: eventData.description,
      url: url,
      startDate: eventData.startDate,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: {
        '@type': 'Place',
        name: eventData.location || eventData.city,
        address: {
          '@type': 'PostalAddress',
          addressLocality: eventData.city
        }
      },
      organizer: {
        '@type': 'Organization',
        name: eventData.organizerName || SITE_NAME,
        url: SITE_URL
      },
      ...(eventData.priceCents !== undefined && eventData.priceCents >= 0 && {
        offers: {
          '@type': 'Offer',
          price: (eventData.priceCents / 100).toFixed(2),
          priceCurrency: eventData.currency || 'EUR',
          availability: eventData.maxParticipants && eventData.currentParticipants !== undefined &&
            eventData.currentParticipants >= eventData.maxParticipants
            ? 'https://schema.org/SoldOut'
            : 'https://schema.org/InStock',
          url: url
        }
      })
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(eventJsonLd);
    document.head.appendChild(script);
  }
  
  // Add BreadcrumbList JSON-LD
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url
      }))
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(breadcrumbJsonLd);
    document.head.appendChild(script);
  }
  
  // Add ItemList JSON-LD (for listing pages)
  if (itemList && itemList.length > 0) {
    const itemListJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      numberOfItems: itemList.length,
      itemListElement: itemList.slice(0, 10).map((item, index) => ({
        '@type': 'ListItem',
        position: item.position || index + 1,
        url: item.url,
        name: item.name,
        ...(item.image && { image: item.image })
      }))
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(itemListJsonLd);
    document.head.appendChild(script);
  }
  
  // Add FAQPage JSON-LD
  if (faqItems && faqItems.length > 0) {
    const faqJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(faqJsonLd);
    document.head.appendChild(script);
  }
  
  // Add Organization JSON-LD
  if (organizationData) {
    const orgJsonLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: organizationData.name,
      ...(organizationData.description && { description: organizationData.description }),
      ...(organizationData.url && { url: organizationData.url }),
      ...(organizationData.logo && { logo: organizationData.logo }),
      ...(organizationData.email && { email: organizationData.email }),
      ...(organizationData.telephone && { telephone: organizationData.telephone }),
      ...(organizationData.address && {
        address: {
          '@type': 'PostalAddress',
          ...(organizationData.address.city && { addressLocality: organizationData.address.city }),
          ...(organizationData.address.country && { addressCountry: organizationData.address.country })
        }
      }),
      ...(organizationData.sameAs && organizationData.sameAs.length > 0 && { sameAs: organizationData.sameAs })
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(orgJsonLd);
    document.head.appendChild(script);
  }
}

export function useSEO({
  title,
  description,
  image,
  url,
  type = 'website',
  eventData,
  breadcrumbs,
  itemList,
  faqItems,
  organizationData
}: SEOProps) {
  useEffect(() => {
    // Store original values for cleanup
    const originalTitle = document.title;
    const originalDescription = document.querySelector('meta[name="description"]')?.getAttribute('content');
    const originalCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
    
    // Update document title
    if (title) {
      document.title = `${title} | ${SITE_NAME}`;
    }
    
    // Build final values
    const finalDescription = description || DEFAULT_DESCRIPTION;
    const finalImage = image || DEFAULT_IMAGE;
    const finalUrl = url || SITE_URL;
    
    // Update meta tags
    updateMetaTag('description', finalDescription, true);
    
    // Open Graph
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', finalDescription);
    updateMetaTag('og:image', finalImage);
    updateMetaTag('og:url', finalUrl);
    updateMetaTag('og:type', type === 'event' ? 'website' : type);
    updateMetaTag('og:site_name', SITE_NAME);
    updateMetaTag('og:locale', 'fr_FR');
    
    // Twitter
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', finalDescription, true);
    updateMetaTag('twitter:image', finalImage, true);
    
    // Canonical URL
    updateCanonicalLink(finalUrl);
    
    // JSON-LD structured data
    updateJsonLd(
      type === 'event' ? eventData : undefined, 
      finalUrl,
      breadcrumbs,
      itemList,
      faqItems,
      organizationData
    );
    
    // Cleanup function
    return () => {
      document.title = originalTitle;
      if (originalDescription) {
        updateMetaTag('description', originalDescription, true);
      }
      if (originalCanonical) {
        updateCanonicalLink(originalCanonical);
      }
      document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach(el => el.remove());
    };
  }, [title, description, image, url, type, eventData, breadcrumbs, itemList, faqItems, organizationData]);
}
